import type { Editor, NodeEntry, Path, Point, Text } from 'slate';
import { Node, Range } from 'slate';

import { isDeleteEdit, isParagraphEntry, Queries } from '../internal';
import type { ParagraphElement, RenderModelParagraph } from '../internal';

const searchQueries = {
  getCMSearchHits(options: {
    cMParagraphEntry: NodeEntry<ParagraphElement>;
    matchAccents: boolean;
    matchCase: boolean;
    search: string;
  }): Range[] {
    const {
      cMParagraphEntry: [cMParagraph, cMParagraphPath],
      matchAccents,
      matchCase,
      search,
    } = options;
    if (search.length === 0) return [];

    const textNodeEntries = getSearchableTextNodeEntries(cMParagraph);

    return Array.from(
      getMatchingRanges(textNodeEntries, {
        basePath: cMParagraphPath,
        matchAccents,
        matchCase,
        search,
      })
    );
  },

  getRMSearchHits(
    editor: Editor,
    options: {
      matchAccents: boolean;
      matchCase: boolean;
      rMParagraphEntry: NodeEntry<RenderModelParagraph>;
      search: string;
    }
  ): Range[] {
    const { matchAccents, matchCase, rMParagraphEntry, search: rawSearch } = options;
    const [rMParagraph, rMParagraphPath] = rMParagraphEntry;
    if (rawSearch.length === 0) return [];

    const textNodeEntries = getSearchableTextNodeEntries(rMParagraph);
    const rawSearchableText = textNodeEntries.map(([{ text }]) => text).join('');

    const search = normalizeString(rawSearch, { matchAccents, matchCase });
    const searchableText = normalizeString(rawSearchableText, {
      matchAccents,
      matchCase,
    });

    /**
     * PERF: Check if any of the following cases applies:
     * 1) the entire paragraph appears as a substring of the search query,
     * 2) the paragraph starts with some substring that appears at the end of the search query, or
     * 3) the paragraph ends with some substring that appears at the start of the search query.
     *
     * If any of these are true, then there may be matches that can only be detected by considering
     * the content model paragraph as a whole. Otherwise, the render model paragraph contains
     * sufficient information to return all possible matches, so there's no need to incur the
     * performance overhead of fetching the content model paragraph and converting any matching
     * ranges back to the render model.
     */
    let useContentModel = search.includes(searchableText);
    for (let substrLength = 1; !useContentModel && substrLength < search.length; substrLength++) {
      const prefix = search.slice(0, substrLength);
      const postfix = search.slice(-substrLength);

      if (searchableText.startsWith(postfix) || searchableText.endsWith(prefix)) {
        useContentModel = true;
      }
    }

    if (useContentModel) {
      const cMParagraphEntry = Queries.nodeFromRMLocation(editor, rMParagraphPath);
      if (!isParagraphEntry(cMParagraphEntry)) throw new Error('Expected paragraph');

      const cMSearchHits = Queries.getCMSearchHits({
        cMParagraphEntry,
        matchAccents,
        matchCase,
        search,
      });

      const rMParagraphRange = Queries.renderModelRange(editor, rMParagraphPath);

      return cMSearchHits.flatMap(cMRange =>
        Queries.getRMRangesFromCMRange(editor, cMRange)
          .flatMap(rMRange => {
            // Only return hits for the current render model paragraph
            const intersection = Range.intersection(rMRange, rMParagraphRange);
            return intersection ? [intersection] : [];
          })
          .toArray()
      );
    }

    return Array.from(
      getMatchingRanges(textNodeEntries, {
        basePath: rMParagraphPath,
        matchAccents,
        matchCase,
        search,
      })
    );
  },
};

export default searchQueries;

function getSearchableTextNodeEntries(paragraph: ParagraphElement | RenderModelParagraph) {
  return Array.from(
    Node.texts(paragraph).filter(([textNode]) => {
      const suggestedEdits = textNode.suggestedEdits;
      return !Array.isArray(suggestedEdits) || !suggestedEdits.some(isDeleteEdit);
    })
  );
}

function normalizeString(value: string, options: { matchAccents: boolean; matchCase: boolean }) {
  const { matchAccents, matchCase } = options;
  let normalizedValue = value;
  if (!matchCase) normalizedValue = normalizedValue.toLowerCase();
  if (!matchAccents) normalizedValue = removeAccents(normalizedValue);
  return normalizedValue;
}

function removeAccents(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function* getMatchingRanges(
  textNodeEntries: NodeEntry<Text>[],
  options: { basePath: Path; matchAccents: boolean; matchCase: boolean; search: string }
): Iterable<Range> {
  if (textNodeEntries.length === 0) return;
  const { basePath, matchAccents, matchCase, search: rawSearch } = options;

  const search = normalizeString(rawSearch, { matchAccents, matchCase });
  const rawSearchableText = textNodeEntries.map(([{ text }]) => text).join('');
  const searchableText = normalizeString(rawSearchableText, {
    matchAccents,
    matchCase,
  });

  let offset = 0;

  while (offset < searchableText.length) {
    const nextMatchIndex = searchableText.indexOf(search, offset);
    if (nextMatchIndex === -1) break;

    const startOffset = nextMatchIndex;
    const endOffset = startOffset + search.length;
    yield { anchor: offsetToPoint(startOffset), focus: offsetToPoint(endOffset) };
    offset = endOffset;
  }

  function offsetToPoint(offset: number): Point {
    let remainingOffset = offset;

    for (const [{ text }, relativePath] of textNodeEntries) {
      if (text.length >= remainingOffset)
        return { offset: remainingOffset, path: basePath.concat(relativePath) };
      remainingOffset -= text.length;
    }

    throw new Error(`Could not get point for offset ${offset}`);
  }
}
